package org.gymly.ui.admin

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.ChevronRight
import androidx.compose.material.icons.outlined.Home
import androidx.compose.material.icons.outlined.Menu
import androidx.compose.material.icons.outlined.Person
import androidx.compose.material.icons.outlined.ShoppingBag
import androidx.compose.material.icons.outlined.Wallet
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.NavigationBar
import androidx.compose.material3.NavigationBarItem
import androidx.compose.material3.NavigationBarItemDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.gymly.i18n.strings
import org.gymly.model.AdminNavGroup
import org.gymly.model.AdminRoute
import org.gymly.theme.AdminColors

/** Destinos de la barra inferior en celular: lo que más se usa. El resto queda en «Más». */
private val RUTAS_BARRA = listOf(AdminRoute.Dashboard, AdminRoute.Payments, AdminRoute.StoreSales, AdminRoute.Clients)

private fun iconoRuta(ruta: AdminRoute): ImageVector = when (ruta) {
    AdminRoute.Dashboard -> Icons.Outlined.Home
    AdminRoute.Payments -> Icons.Outlined.Wallet
    AdminRoute.StoreSales -> Icons.Outlined.ShoppingBag
    AdminRoute.Clients -> Icons.Outlined.Person
    else -> Icons.Outlined.ChevronRight
}

/**
 * Barra inferior del admin en celular, con el estilo de Material de Android.
 * Solo muestra los destinos que el usuario puede abrir; el panel siempre está.
 */
@Composable
fun AdminBarraInferior(
    current: AdminRoute,
    canOpen: (AdminRoute) -> Boolean,
    onSelect: (AdminRoute) -> Unit,
    onMas: () -> Unit,
    masActivo: Boolean,
) {
    val nav = strings().nav
    NavigationBar(containerColor = AdminColors.Surface) {
        RUTAS_BARRA.filter { it == AdminRoute.Dashboard || canOpen(it) }.forEach { ruta ->
            NavigationBarItem(
                selected = current == ruta,
                onClick = { onSelect(ruta) },
                icon = { Icon(iconoRuta(ruta), contentDescription = nav.route(ruta)) },
                label = {
                    Text(nav.route(ruta), maxLines = 1, overflow = TextOverflow.Ellipsis, fontSize = 11.sp)
                },
                colors = coloresBarra(),
            )
        }
        NavigationBarItem(
            selected = masActivo,
            onClick = onMas,
            icon = { Icon(Icons.Outlined.Menu, contentDescription = "Más módulos") },
            label = { Text("Más", fontSize = 11.sp) },
            colors = coloresBarra(),
        )
    }
}

@Composable
private fun coloresBarra() = NavigationBarItemDefaults.colors(
    selectedIconColor = AdminColors.Primary,
    selectedTextColor = AdminColors.Primary,
    indicatorColor = AdminColors.Primary.copy(alpha = 0.14f),
    unselectedIconColor = AdminColors.TextMuted,
    unselectedTextColor = AdminColors.TextMuted,
)

/**
 * «Más» en celular: el resto de módulos agrupados como en el menú lateral, dentro de una hoja inferior.
 * Se respetan las mismas reglas de visibilidad y permisos que el menú lateral.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AdminHojaMas(
    current: AdminRoute,
    showReports: Boolean,
    showSettings: Boolean,
    showTermsMonitoring: Boolean,
    clientAccountsEnabled: Boolean,
    pendingApprovalsCount: Long,
    canOpen: (AdminRoute) -> Boolean,
    onSelect: (AdminRoute) -> Unit,
    onDismiss: () -> Unit,
) {
    val nav = strings().nav
    fun visible(ruta: AdminRoute): Boolean = ruta !in RUTAS_BARRA && when (ruta) {
        AdminRoute.Reports -> showReports && canOpen(ruta)
        AdminRoute.Settings -> showSettings && canOpen(ruta)
        AdminRoute.TermsMonitoring -> showTermsMonitoring && canOpen(ruta)
        AdminRoute.ClientAccounts -> clientAccountsEnabled && canOpen(ruta)
        AdminRoute.Profile -> true
        else -> canOpen(ruta)
    }

    ModalBottomSheet(onDismissRequest = onDismiss, containerColor = AdminColors.Surface) {
        Column(Modifier.fillMaxWidth().verticalScroll(rememberScrollState()).padding(bottom = 24.dp)) {
            val generales = listOf(AdminRoute.BulkSignup, AdminRoute.Reports).filter { it.group == null && visible(it) }
            if (generales.isNotEmpty()) {
                SeccionMas("General", generales, current, pendingApprovalsCount, onSelect)
            }
            AdminNavGroup.entries.forEach { grupo ->
                val rutas = AdminRoute.entries.filter { it.group == grupo && visible(it) }
                if (rutas.isNotEmpty()) {
                    SeccionMas(nav.group(grupo), rutas, current, pendingApprovalsCount, onSelect)
                }
            }
        }
    }
}

@Composable
private fun SeccionMas(
    titulo: String,
    rutas: List<AdminRoute>,
    current: AdminRoute,
    pendingApprovalsCount: Long,
    onSelect: (AdminRoute) -> Unit,
) {
    Text(
        titulo.uppercase(),
        color = AdminColors.Primary,
        fontWeight = FontWeight.Bold,
        fontSize = 12.sp,
        letterSpacing = 0.8.sp,
        modifier = Modifier.padding(start = 20.dp, end = 20.dp, top = 16.dp, bottom = 4.dp),
    )
    rutas.forEach { ruta ->
        val seleccionada = ruta == current
        val aviso = if (ruta == AdminRoute.Settings) pendingApprovalsCount else 0L
        Row(
            Modifier
                .fillMaxWidth()
                .heightIn(min = 52.dp)
                .clickable { onSelect(ruta) }
                .padding(horizontal = 20.dp, vertical = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Icon(
                iconoRuta(ruta),
                contentDescription = null,
                tint = if (seleccionada) AdminColors.Primary else AdminColors.TextMuted,
                modifier = Modifier.size(22.dp),
            )
            Spacer(Modifier.width(16.dp))
            Text(
                strings().nav.route(ruta),
                color = if (seleccionada) AdminColors.Primary else AdminColors.Text,
                fontWeight = if (seleccionada) FontWeight.SemiBold else FontWeight.Normal,
                fontSize = 15.sp,
                modifier = Modifier.weight(1f),
            )
            if (aviso > 0L) {
                Text(aviso.toString(), color = AdminColors.Error, fontWeight = FontWeight.Bold, fontSize = 13.sp)
            }
        }
    }
}
